const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Formats an ISO timestamp as a short relative age ("3 days ago").
 *
 * Anything older than a month reads as an absolute date instead — "47 days
 * ago" is harder to place than "12 Aug 2025", and a release feed is scanned
 * for recency at the top and for identity further down.
 *
 * Returns an empty string for a missing or unparseable timestamp so callers
 * can render the surrounding line unconditionally.
 */
export function formatRelativeDate(
  iso: string,
  now: Date = new Date(),
): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) {
    return '';
  }

  const elapsed = now.getTime() - then.getTime();

  // A clock skew between the browser and GitHub can put a fresh release
  // marginally in the future; read that as "just now" rather than negative.
  if (elapsed < MINUTE) {
    return 'just now';
  }
  if (elapsed < HOUR) {
    const minutes = Math.floor(elapsed / MINUTE);
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }
  if (elapsed < DAY) {
    const hours = Math.floor(elapsed / HOUR);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  if (elapsed < 30 * DAY) {
    const days = Math.floor(elapsed / DAY);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }

  return then.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
